'use client'

import { useEffect, useState } from 'react'

import { format } from 'date-fns'
import { AnimatePresence, motion } from 'framer-motion'
import {
  Calendar,
  ChevronDown,
  ChevronUp,
  Clock,
  Filter,
  MapPin,
  Monitor,
  Search,
  Star,
  Users,
} from 'lucide-react'

import {
  Workshop,
  WorkshopInstance,
  WorkshopPreference,
  useWorkshopInstances,
  useWorkshops,
} from '../../lib/workshop-api'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { Card } from '../ui/card'
import { Input } from '../ui/input'
import { Select } from '../ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs'

interface WorkshopSelectionProps {
  workshopId?: string
  selectedPreferences: WorkshopPreference[]
  onSelectionChange: (preferences: WorkshopPreference[]) => void
  onSubmit: () => void
  loading?: boolean
}

export function WorkshopSelection({
  workshopId,
  selectedPreferences,
  onSelectionChange,
  onSubmit,
  loading = false,
}: WorkshopSelectionProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [categoryFilter, setCategoryFilter] = useState<string>('all')
  const [difficultyFilter, setDifficultyFilter] = useState<string>('all')
  const [formatFilter, setFormatFilter] = useState<string>('all')
  const [expandedWorkshops, setExpandedWorkshops] = useState<Set<string>>(new Set())

  const { workshops, loading: workshopsLoading } = useWorkshops({
    category: categoryFilter !== 'all' ? categoryFilter : undefined,
    difficulty: difficultyFilter !== 'all' ? difficultyFilter : undefined,
    format: formatFilter !== 'all' ? formatFilter : undefined,
  })

  const { instances, loading: instancesLoading } = useWorkshopInstances(workshopId)

  // Filter workshops based on search term
  const filteredWorkshops = workshops.filter(
    workshop =>
      workshop.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      workshop.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      workshop.category.toLowerCase().includes(searchTerm.toLowerCase())
  )

  // Get instances for filtered workshops
  const getWorkshopInstances = (workshopId: string): WorkshopInstance[] => {
    return instances.filter(
      instance =>
        instance.workshopId === workshopId &&
        instance.status === 'scheduled' &&
        new Date(instance.startDate) > new Date()
    )
  }

  const handlePreferenceChange = (
    workshopInstanceId: string,
    preference: number,
    format: 'in-person' | 'online',
    title: string
  ) => {
    const existing = selectedPreferences.find(p => p.workshopInstanceId === workshopInstanceId)
    const newPreferences = selectedPreferences.filter(
      p => p.workshopInstanceId !== workshopInstanceId
    )

    if (existing && existing.preference === preference && existing.format === format) {
      // Remove if clicking the same preference
      onSelectionChange(newPreferences)
    } else {
      // Add or update preference
      onSelectionChange([...newPreferences, { workshopInstanceId, preference, format, title }])
    }
  }

  const getPreferenceForInstance = (workshopInstanceId: string) => {
    return selectedPreferences.find(p => p.workshopInstanceId === workshopInstanceId)
  }

  const toggleWorkshopExpansion = (workshopId: string) => {
    const newExpanded = new Set(expandedWorkshops)
    if (newExpanded.has(workshopId)) {
      newExpanded.delete(workshopId)
    } else {
      newExpanded.add(workshopId)
    }
    setExpandedWorkshops(newExpanded)
  }

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner':
        return 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-300'
      case 'intermediate':
        return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-300'
      case 'advanced':
        return 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-300'
      default:
        return 'bg-gray-100 text-gray-700 dark:bg-gray-900/20 dark:text-gray-300'
    }
  }

  const canSubmit = selectedPreferences.length > 0

  if (workshopsLoading || instancesLoading) {
    return (
      <Card className="p-8 text-center">
        <div className="space-y-4">
          <div className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-shuttle-red border-t-transparent" />
          <p className="text-gray-600 dark:text-gray-400">Loading workshops...</p>
        </div>
      </Card>
    )
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="space-y-6"
    >
      {/* Header */}
      <Card className="p-6">
        <div className="space-y-4 text-center">
          <h2 className="text-2xl font-semibold text-shuttle-navy dark:text-white">
            Choose Your Workshops
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Select your preferred workshops and indicate your preference order. We'll try to assign
            you to your highest preference available workshops.
          </p>
        </div>

        {/* Search and Filters */}
        <div className="mt-6 space-y-4">
          <div className="flex flex-col gap-4 md:flex-row">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-gray-400" />
              <Input
                placeholder="Search workshops..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Filters */}
            <div className="flex gap-2">
              <Select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}>
                <option value="all">All Categories</option>
                <option value="data-engineering">Data Engineering</option>
                <option value="analytics">Analytics</option>
                <option value="ml-ai">ML & AI</option>
                <option value="cloud">Cloud</option>
              </Select>

              <Select value={difficultyFilter} onChange={e => setDifficultyFilter(e.target.value)}>
                <option value="all">All Levels</option>
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
              </Select>

              <Select value={formatFilter} onChange={e => setFormatFilter(e.target.value)}>
                <option value="all">All Formats</option>
                <option value="in-person">In-Person</option>
                <option value="online">Online</option>
                <option value="hybrid">Hybrid</option>
              </Select>
            </div>
          </div>
        </div>
      </Card>

      {/* Selected Preferences Summary */}
      {selectedPreferences.length > 0 && (
        <Card className="border-shuttle-red/20 bg-shuttle-red/5 p-6">
          <h3 className="mb-4 text-lg font-medium text-shuttle-navy dark:text-white">
            Your Workshop Preferences ({selectedPreferences.length})
          </h3>
          <div className="grid gap-2">
            {selectedPreferences
              .sort((a, b) => a.preference - b.preference)
              .map((pref, index) => (
                <div
                  key={pref.workshopInstanceId}
                  className="flex items-center justify-between rounded-lg bg-white p-3 dark:bg-gray-800"
                >
                  <div className="flex items-center space-x-3">
                    <Badge
                      variant="outline"
                      className="flex h-8 w-8 items-center justify-center rounded-full"
                    >
                      {pref.preference}
                    </Badge>
                    <div>
                      <div className="font-medium">{pref.title}</div>
                      <div className="text-sm capitalize text-gray-600 dark:text-gray-400">
                        {pref.format} format
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      onSelectionChange(
                        selectedPreferences.filter(
                          p => p.workshopInstanceId !== pref.workshopInstanceId
                        )
                      )
                    }}
                  >
                    Remove
                  </Button>
                </div>
              ))}
          </div>
        </Card>
      )}

      {/* Workshop List */}
      <div className="space-y-4">
        {filteredWorkshops.map(workshop => {
          const workshopInstances = getWorkshopInstances(workshop.id)
          const isExpanded = expandedWorkshops.has(workshop.id)

          if (workshopInstances.length === 0) return null

          return (
            <Card key={workshop.id} className="overflow-hidden">
              <div className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1 space-y-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="text-xl font-semibold text-shuttle-navy dark:text-white">
                          {workshop.title}
                        </h3>
                        <div className="mt-2 flex items-center space-x-3">
                          <Badge className={getDifficultyColor(workshop.difficultyLevel)}>
                            {workshop.difficultyLevel}
                          </Badge>
                          <Badge variant="outline">{workshop.category.replace('-', ' ')}</Badge>
                          <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                            <Clock className="mr-1 h-4 w-4" />
                            {workshop.durationHours}h
                          </div>
                        </div>
                      </div>

                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleWorkshopExpansion(workshop.id)}
                      >
                        {isExpanded ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                      </Button>
                    </div>

                    <p className="line-clamp-2 text-gray-600 dark:text-gray-400">
                      {workshop.description}
                    </p>

                    {/* Show first instance as preview */}
                    {!isExpanded && workshopInstances.length > 0 && (
                      <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400">
                        <div className="flex items-center">
                          <Calendar className="mr-1 h-4 w-4" />
                          {format(new Date(workshopInstances[0].startDate), 'MMM d, yyyy')}
                        </div>
                        {workshopInstances.length > 1 && (
                          <span>+{workshopInstances.length - 1} more dates</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Expanded Instance Details */}
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="mt-6 space-y-4"
                    >
                      <div className="grid gap-4">
                        {workshopInstances.map(instance => {
                          const currentPreference = getPreferenceForInstance(instance.id)

                          return (
                            <div
                              key={instance.id}
                              className="rounded-lg border bg-gray-50 p-4 dark:bg-gray-800/50"
                            >
                              <div className="mb-3 flex items-center justify-between">
                                <div className="space-y-1">
                                  <div className="flex items-center space-x-4 text-sm">
                                    <div className="flex items-center">
                                      <Calendar className="mr-1 h-4 w-4" />
                                      {format(new Date(instance.startDate), 'MMM d, yyyy')} -{' '}
                                      {format(new Date(instance.endDate), 'MMM d, yyyy')}
                                    </div>
                                    <div className="flex items-center">
                                      <Clock className="mr-1 h-4 w-4" />
                                      {format(new Date(instance.startDate), 'h:mm a')}
                                    </div>
                                  </div>
                                  <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400">
                                    {instance.format === 'in-person' ||
                                    instance.format === 'hybrid' ? (
                                      <div className="flex items-center">
                                        <MapPin className="mr-1 h-4 w-4" />
                                        {workshop.location || 'Location TBA'}
                                      </div>
                                    ) : (
                                      <div className="flex items-center">
                                        <Monitor className="mr-1 h-4 w-4" />
                                        Online
                                      </div>
                                    )}
                                  </div>
                                </div>

                                <div className="flex items-center space-x-2">
                                  {(instance.format === 'in-person' ||
                                    instance.format === 'hybrid') && (
                                    <div className="text-sm text-gray-600 dark:text-gray-400">
                                      <Users className="mr-1 inline h-4 w-4" />
                                      {instance.currentInPersonSpots}/{instance.maxInPersonSpots}{' '}
                                      in-person
                                    </div>
                                  )}
                                  {(instance.format === 'online' ||
                                    instance.format === 'hybrid') && (
                                    <div className="text-sm text-gray-600 dark:text-gray-400">
                                      <Monitor className="mr-1 inline h-4 w-4" />
                                      {instance.currentOnlineSpots}/{instance.maxOnlineSpots} online
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Format Selection */}
                              <div className="space-y-3">
                                {(instance.format === 'in-person' ||
                                  instance.format === 'hybrid') && (
                                  <div>
                                    <h4 className="mb-2 text-sm font-medium">In-Person Format</h4>
                                    <div className="flex items-center space-x-2">
                                      <div className="text-sm text-gray-600 dark:text-gray-400">
                                        ${workshop.inPersonPrice} • Available spots:{' '}
                                        {instance.maxInPersonSpots - instance.currentInPersonSpots}
                                      </div>
                                    </div>
                                    <div className="mt-2 flex space-x-2">
                                      {[1, 2, 3].map(pref => (
                                        <Button
                                          key={`${instance.id}-in-person-${pref}`}
                                          variant={
                                            currentPreference?.preference === pref &&
                                            currentPreference?.format === 'in-person'
                                              ? 'default'
                                              : 'outline'
                                          }
                                          size="sm"
                                          onClick={() =>
                                            handlePreferenceChange(
                                              instance.id,
                                              pref,
                                              'in-person',
                                              workshop.title
                                            )
                                          }
                                        >
                                          <Star className="mr-1 h-3 w-3" />
                                          {pref}
                                        </Button>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {(instance.format === 'online' || instance.format === 'hybrid') && (
                                  <div>
                                    <h4 className="mb-2 text-sm font-medium">Online Format</h4>
                                    <div className="flex items-center space-x-2">
                                      <div className="text-sm text-gray-600 dark:text-gray-400">
                                        ${workshop.onlinePrice} • Available spots:{' '}
                                        {instance.maxOnlineSpots - instance.currentOnlineSpots}
                                      </div>
                                    </div>
                                    <div className="mt-2 flex space-x-2">
                                      {[1, 2, 3].map(pref => (
                                        <Button
                                          key={`${instance.id}-online-${pref}`}
                                          variant={
                                            currentPreference?.preference === pref &&
                                            currentPreference?.format === 'online'
                                              ? 'default'
                                              : 'outline'
                                          }
                                          size="sm"
                                          onClick={() =>
                                            handlePreferenceChange(
                                              instance.id,
                                              pref,
                                              'online',
                                              workshop.title
                                            )
                                          }
                                        >
                                          <Star className="mr-1 h-3 w-3" />
                                          {pref}
                                        </Button>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </Card>
          )
        })}
      </div>

      {filteredWorkshops.length === 0 && (
        <Card className="p-8 text-center">
          <div className="space-y-4">
            <Filter className="mx-auto h-12 w-12 text-gray-400" />
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                No workshops found
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Try adjusting your search terms or filters.
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Submit Button */}
      <div className="flex justify-end">
        <Button
          onClick={onSubmit}
          disabled={!canSubmit || loading}
          size="lg"
          className="bg-shuttle-red hover:bg-shuttle-red/90"
        >
          {loading ? (
            <div className="flex items-center space-x-2">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
              <span>Processing...</span>
            </div>
          ) : (
            `Review Selection (${selectedPreferences.length})`
          )}
        </Button>
      </div>
    </motion.div>
  )
}
